<template>
  <div>
    <form-wizard
      color="#498ceb"
      :title="null"
      :subtitle="null"
      layout="vertical"
      :finish-button-text="$t('message.submit')"
      :back-button-text="$t('message.previous')"
      :next-button-text="$t('message.next')"
      class="wizard-vertical mb-3"
      ref="myForm"
    >
      <!-- Buttons personalization -->
      <template v-slot:footer="slotProps">
        <div class="wizard-footer d-flex justify-content-between align-items-center">
          <b-button
            variant="outline-secondary"
            @click="goToPreviousTab"
            :disabled="slotProps.activeTabIndex === 0"
          >
            < &nbsp; {{ $t('message.previous') }}
          </b-button>

          <b-button
            v-if="!slotProps.isLastStep"
            variant="primary"
            @click="goToNextTab"
          >
            {{ $t('message.next') }} &nbsp; >
          </b-button>

          <b-button
            v-else
            variant="success"
            @click="handleSubmit"
          >
            {{ $t('message.submit') }}
          </b-button>
        </div>
      </template>

      <!-- Identification -->
      <tab-content
        :title="$t('label.identification')"
      >
        <validation-observer ref="identificationTab" tag="form">
          <Identification ref="identification" :data="data" />
        </validation-observer>
      </tab-content>

      <!-- Responsibilities -->
      <tab-content
        :title="$t('label.responsibilities')"
      >
        <validation-observer ref="responsibilitiesTab" tag="form">
          <Responsibilities
            ref="responsibilities"
            :responsibilities="data ? data.responsibilities : []"
          />
        </validation-observer>
      </tab-content>

      <!-- Qualifications -->
      <tab-content
        :title="$t('label.qualifications')"
      >
        <validation-observer ref="qualificationsTab" tag="form">
          <Qualifications
            ref="qualifications"
            :qualifications="data ? data.qualifications : []"
          />
        </validation-observer>
      </tab-content>

      <!-- Subroles -->
      <tab-content
        :title="$t('label.subroles')"
      >
        <validation-observer ref="subrolesTab" tag="form">
          <Subroles
            ref="subroles"
            :subroles="data ? data.subroles : []"
            @input="updateSubroles"
          />
        </validation-observer>
      </tab-content>
    </form-wizard>

    <!-- Logs de auditoría -->
    <AuditLogTable
      v-if="isEdit && data"
      :history="data.history || []"
      :field-translations="{
        name: $t('fields.name'),
        code: $t('fields.code'),
        description: $t('fields.description'),
        subroles: $t('label.subroles'),
        qualifications: $t('label.qualifications'),
        responsibilities: $t('label.responsibilities')
      }"
    />
  </div>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import { FormWizard, TabContent } from "vue-form-wizard";
import "vue-form-wizard/dist/vue-form-wizard.min.css";
import useNotifications from "@/composables/useNotifications"
import Identification from "@/views/organization/role/shared/Identification";
import Responsibilities from "@/views/organization/role/shared/Responsibilities";
import Qualifications from "@/views/organization/role/shared/Qualifications";
import Subroles from "@/views/organization/role/shared/Subroles";
import AuditLogTable from "@/components/AuditLogTable.vue";
import { ref } from "@vue/composition-api";
import i18n from '@/libs/i18n'

export default {
  components: {
    Identification,
    Responsibilities,
    Qualifications,
    Subroles,
    FormWizard,
    TabContent,
    ValidationObserver,
    AuditLogTable,
  },

  props: {
    data: {
      type: Object,
      default: null,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const { showSuccessMessage, showErrorMessage } = useNotifications();
    const _id = ref(null);
    const identification = ref(null);
    const responsibilities = ref(null);
    const qualifications = ref(null);
    const subroles = ref(null);
    const identificationTab = ref(null);
    const responsibilitiesTab = ref(null);
    const qualificationsTab = ref(null);
    const subrolesTab = ref(null);
    const myForm = ref(null)
    const tabRefs = [ identificationTab, responsibilitiesTab, qualificationsTab, subrolesTab ]

    const updateSubroles = (payload) => {
      subroles.value = payload
    }

    const handleSubmit = async () => {
      try {
        await validateTab(subrolesTab)
        formSubmitted()
      } catch (error) {
        // Si alguna validación falla, no enviamos
      }
    }

    const formSubmitted = async () => {
      if (props.isSubmitting) return

      const payload = {
        ...identification.value.getData(),
        ...responsibilities.value.getData(),
        ...qualifications.value.getData(),
        subroles: subroles.value.subrolesData,
      };

      emit("submit", payload);
    };

    const validateTab = (tabRef) => new Promise((resolve, reject) => {
      tabRef.value.validate().then(success => {
        if (success) resolve(true)
        else {
          showErrorMessage(i18n.t('message.requiredFieldsIncomplete'))
          reject()
        }
      })
    })

    const goToPreviousTab = () => {
      if (myForm.value) {
        myForm.value.prevTab()
      }
    }

    const goToNextTab = async () => {
      const index = myForm.value.activeTabIndex

      try {
        await validateTab(tabRefs[index])
        myForm.value.nextTab()
      } catch (err) {
        // Error de validación, no avanza
      }
    }

    return {
      identification,
      responsibilities,
      qualifications,
      subroles,
      updateSubroles,
      identificationTab,
      responsibilitiesTab,
      qualificationsTab,
      subrolesTab,
      myForm,
      goToPreviousTab,
      goToNextTab,
      handleSubmit,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-wizard.scss";
@import "@core/scss/vue/libs/vue-select.scss";
</style>
